/**
 * API client for Admin Center → Maintenance windows (CTX-1294).
 *
 * Every endpoint here is Global Admin only on the backend; the page is gated
 * the same way upstream (`getMaintenanceWindowsAllowedTenantTypes`).
 */
import type { AxiosInstance } from "axios";
import { parseAxiosError } from "@shared/components/utils/apiErrorParser";
import type {
	MaintenanceRecordRow,
	MaintenanceRecordStatusFilter,
	MaintenanceRecordUpdatePayload,
	MaintenanceWindowPayload,
	TenantOption,
	TenantServiceOption,
} from "../types";

const ADMIN_MOUNT = "/kontroledge/service-maintenance/admin";

/**
 * The backend wraps every payload as `{ success, message, data }`. Unwrap it
 * here so callers never deal with the envelope, and fail loudly when the
 * shape is not what we expect instead of surfacing `undefined` downstream.
 */
function unwrap<T>(body: unknown, context: string): T {
	const envelope = body as { success?: boolean; message?: string; data?: T };
	if (!envelope || typeof envelope !== "object" || envelope.data === undefined) {
		throw new Error(`${context}: unexpected response shape`);
	}
	if (envelope.success === false) {
		throw new Error(envelope.message || `${context}: request failed`);
	}
	return envelope.data;
}

/**
 * A failed request as one readable line. A 400 from these endpoints carries
 * every validation error in `data.errors`; the generic parser only reads
 * `message`, which would leave the operator guessing which field.
 */
export function describeRequestError(error: unknown): string {
	const errors = (
		error as { response?: { data?: { data?: { errors?: unknown } } } }
	)?.response?.data?.data?.errors;
	const message = parseAxiosError(error).message;
	if (Array.isArray(errors) && errors.length > 0) {
		return `${message}: ${errors.map(String).join("; ")}`;
	}
	return message;
}

/** `GET /admin?status=` — one row per customer. */
export async function listMaintenanceRecords(
	api: AxiosInstance,
	status: MaintenanceRecordStatusFilter
): Promise<MaintenanceRecordRow[]> {
	const response = await api.get(ADMIN_MOUNT, { params: { status } });
	return unwrap<{ items: MaintenanceRecordRow[] }>(response.data, "listMaintenanceRecords").items;
}

/**
 * `GET /admin/services?tenantIds=` — every KE service of the chosen customers.
 * No request for an empty selection: the answer is known to be empty.
 */
export async function listTenantServices(
	api: AxiosInstance,
	tenantIds: number[]
): Promise<TenantServiceOption[]> {
	if (tenantIds.length === 0) return [];
	const response = await api.get(`${ADMIN_MOUNT}/services`, {
		params: { tenantIds: tenantIds.join(",") },
	});
	return unwrap<{ items: TenantServiceOption[] }>(response.data, "listTenantServices").items;
}

/** `POST /admin` — one row per customer, created together or not at all. */
export async function createMaintenanceWindow(
	api: AxiosInstance,
	payload: MaintenanceWindowPayload
): Promise<MaintenanceRecordRow[]> {
	const response = await api.post(ADMIN_MOUNT, payload);
	return unwrap<{ items: MaintenanceRecordRow[] }>(response.data, "createMaintenanceWindow").items;
}

/** `PUT /admin/:id` — edits one customer's row only. */
export async function updateMaintenanceRecord(
	api: AxiosInstance,
	id: number,
	payload: MaintenanceRecordUpdatePayload
): Promise<MaintenanceRecordRow> {
	const response = await api.put(`${ADMIN_MOUNT}/${id}`, payload);
	return unwrap<MaintenanceRecordRow>(response.data, "updateMaintenanceRecord");
}

/** `DELETE /admin/:id` — that customer stops seeing the window. */
export async function deleteMaintenanceRecord(api: AxiosInstance, id: number): Promise<void> {
	const response = await api.delete(`${ADMIN_MOUNT}/${id}`);
	unwrap<unknown>(response.data, "deleteMaintenanceRecord");
}

/** The raw row of `/resource-manager/tenants/ids-names`. */
interface TenantIdName {
	id: number;
	Name: string;
	nameDisplay?: string | null;
	chargebeeCustomerId?: string | null;
}

/**
 * `GET /resource-manager/tenants/ids-names` — every tenant, for the customer
 * picker. Not narrowed to `tenantTypeId=3`: a partner can own services of its
 * own, and Networking's notices name it like any other customer.
 */
export async function listTenantOptions(api: AxiosInstance): Promise<TenantOption[]> {
	const response = await api.get("/resource-manager/tenants/ids-names");
	return unwrap<TenantIdName[]>(response.data, "listTenantOptions").map((tenant) => ({
		id: tenant.id,
		name: tenant.nameDisplay?.trim() || tenant.Name,
		chargebeeCustomerId: tenant.chargebeeCustomerId ?? null,
	}));
}
